import { createGlobalStyle } from 'styled-components';
import { BOTTOM_POPUP_H } from 'util/variables';
import { fontFamilyMedium } from './fonts';

// Pretendard 웹폰트 CDN 경로는 실행환경별 env(VITE_APP_BLOB_STORAGE_CDN_URL: /prod·/dev·/stage·/local)로 주입한다.
const CDN_BASE_URL = import.meta.env.VITE_APP_BLOB_STORAGE_CDN_URL;

// woff2 1종의 src 폴백 체인(로컬 설치본 → 환경별 CDN → 앱 서버 로컬)을 생성한다.
const fontSrc = (family: string, file: string) => `
    local('${family}'),
    url('${CDN_BASE_URL}/fonts/${file}') format('woff2'),
    url('/fonts/${file}') format('woff2')`;

export const GlobalStyle = createGlobalStyle`
  /* Pretendard 웹폰트 — 1순위 로컬 설치본, 2순위 환경별 CDN, 3순위 앱 서버 로컬(/fonts) 폴백 */
  @font-face {
    font-family: 'Pretendard Bold';
    src: ${fontSrc('Pretendard Bold', 'Pretendard-Bold.woff2')};
    font-display: swap;
  }

  @font-face {
    font-family: 'Pretendard Medium';
    src: ${fontSrc('Pretendard Medium', 'Pretendard-Medium.woff2')};
    font-display: swap;
  }

  @font-face {
    font-family: 'Pretendard Regular';
    src: ${fontSrc('Pretendard Regular', 'Pretendard-Regular.woff2')};
    font-display: swap;
  }

  @font-face {
    font-family: 'Pretendard SemiBold';
    src: ${fontSrc('Pretendard SemiBold', 'Pretendard-SemiBold.woff2')};
    font-display: swap;
  }

  html {
    height: 100%;
    width: 100%;
    font-size: 62.5%;
    ${fontFamilyMedium};
    color: #2A2C2F;
  }

  /*
   * 우하단 플로팅 요소(AI 챗봇 런처 / GoToTop) 세로 스택 오프셋.
   *
   * AppContainer가 아니라 html에 선언한다 — AI 챗봇 위젯 루트는 벤더 스크립트가
   * document.body에 직접 붙이므로 AppContainer의 하위가 아니고, 거기에 두면 위젯이
   * 변수를 상속받지 못해 조용히 실패한다.
   *
   * 아래에서 위로: GoToTop(56px) → AI 챗봇 런처. 하단 팝업이 있으면 둘 다 그만큼 올라간다.
   */
  html {
    --ssq-fab-bottom: 2.4rem;
    --ssq-popup-h: 0rem;
    --ssq-gototop-slot: 0rem;
  }

  html:has(.bottom-popup) {
    --ssq-popup-h: ${BOTTOM_POPUP_H.web};
  }

  /* GoToTop은 스크롤 최상단에서 .hidden(display:none)으로 숨으므로 자리도 비워야 한다.
     GoToTop은 rem이 아니라 에셋 고유 크기(56px 고정)로 렌더되므로 이 슬롯도 px로 둔다 —
     rem으로 두면 모바일(root 9px)에서 간격이 8px보다 좁아진다. */
  html:has(.ssq-gototop:not(.hidden)) {
    --ssq-gototop-slot: 64px; /* GoToTop 56px + 간격 8px */
  }

  /* 벤더가 bottom을 인라인 스타일로 지정하므로 !important가 필요하다. */
  #lse-electric-ai-chatbot-root {
    bottom: calc(var(--ssq-fab-bottom) + var(--ssq-popup-h) + var(--ssq-gototop-slot)) !important;
  }

  /* 모바일 메인메뉴가 열려 있는 동안(오버레이 마커 존재 시) 우하단 AI 챗봇 런처를 숨긴다.
     오버레이는 모바일에서 메뉴가 열렸을 때만 렌더되므로 마커 존재 = 모바일 메뉴 열림이다.
     벤더가 display를 인라인으로 두므로 !important가 필요하다. */
  html:has(.ssq-mainmenu-overlay) #lse-electric-ai-chatbot-root {
    display: none !important;
  }

  /* 모바일 검색창(MobileSrchInput) 모달이 열려 있는 동안 우하단 AI 챗봇 런처를 모달 뒤로 보낸다.
     모달 오버레이 z-index는 10001(ctls/modal/Modal.tsx)이므로 그보다 낮춰 뒤로 깔리게 한다.
     벤더가 z-index를 인라인으로 두므로 !important가 필요하다. */
  html:has(.ssq-mobile-srch-overlay) #lse-electric-ai-chatbot-root {
    z-index: 10000 !important;
  }

  /*
   * 열린 채팅 패널 높이 보정.
   *
   * 벤더는 패널을 루트 기준 absolute + max-height: calc(100vh - 32px)로 두는데, 이 값은
   * 루트가 하단 24px에 있다는 가정에서 나온 것이다(상단 여백 8px). 루트를 팝업/GoToTop만큼
   * 올리면 패널이 그만큼 위로 넘쳐 상단이 잘리므로 같은 양을 캡에서 덜어낸다.
   * 오프셋이 0이면 벤더 값과 정확히 같아지므로 기존 동작에는 영향이 없다.
   *
   * 벤더는 768px 미만에서 패널을 position: fixed(뷰포트 기준)로 전환하므로 그 구간은 제외한다.
   */
  @media screen and (min-width: 768px) {
    #lse-electric-ai-chatbot-root > div {
      max-height: calc(
        100vh - var(--ssq-fab-bottom) - var(--ssq-popup-h) - var(--ssq-gototop-slot) - 0.8rem
      ) !important;
    }
  }

  //ios notch 영역 여백
  body {
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
    padding: constant(safe-area-inset-top) constant(safe-area-inset-right) constant(safe-area-inset-bottom) constant(safe-area-inset-left);
  }

  .visuallyhidden {
    border: 0 none;
    clip: rect(0px, 0px, 0px, 0px);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  * {
    padding: 0;
    margin: 0;
    outline: 0;
    border: 0;
    background: transparent;
    box-sizing: border-box;
    list-style: none;
    text-decoration: none;
    letter-spacing: -0.2px;
  }


  button, input[type="radio"], select {
    cursor: pointer;
  }

  select {
    height: 3.2rem;
    color: #2A2C2F;

    &:disabled {
      cursor: inherit;
    }
  }

  .swiper-button-arrow:after, .swiper-button-next:after, .swiper-button-prev:after {
    display: none;
  }

  *::-webkit-scrollbar {
    width: 9px;
    height: 9px;

    &-thumb {
      background-color: #7783A1;
      border-radius: 6px;
    }

    &-track {
      background-color: transparent;
    }

    &-button {
      width: 0;
      height: 0;
    }
  }

  .container {
    max-width: 120rem;
    margin: 0 auto;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 600 !important;
  }

  input[type="text"], input[type="password"] {
    -webkit-appearance: none;
    ${fontFamilyMedium};
  }

  textarea {
    resize: none;
    ${fontFamilyMedium};
  }

  @media screen and (max-width: 1199px) {
    .container {
      max-width: 100% !important;
    }
  }

  @media screen and (max-width: 764px) {

    html {
      font-size: 9px;
    }
  }

  /* 하단 팝업 높이 전환 지점은 BottomPopupArea와 동일하게 768px로 맞춘다. */
  @media screen and (max-width: 768px) {
    html:has(.bottom-popup) {
      --ssq-popup-h: ${BOTTOM_POPUP_H.mobile};
    }
  }
`;
